<template>
  <section class="relative overflow-hidden">
    <div
      class="container mx-auto flex flex-col-reverse items-center gap-4 px-6 pb-12 pt-2 md:flex-row md:gap-6 md:pb-28 md:pt-20"
    >
      <div class="w-full text-center md:w-1/2 md:text-left">
        <p class="enter font-general-medium text-lg text-gray-400 md:text-xl">Hey there, I am</p>
        <h1
          class="enter mb-3 mt-1 font-general-semibold text-[clamp(2.6rem,8vw,4.5rem)] leading-none text-ternary-dark dark:text-primary-light [text-indent:-0.04em]"
          :style="{ '--enter-delay': '80ms' }"
        >
          Kadir Aslan.
        </h1>

        <p
          class="enter flex flex-wrap justify-center gap-x-1 font-general-semibold text-[clamp(1.4rem,4vw,2.1rem)] md:justify-start"
          :style="{ '--enter-delay': '160ms' }"
        >
          <template v-for="(craft, i) in crafts" :key="craft.id">
            <span class="text-gray-400">{{
              i === 0 ? 'I' : i === crafts.length - 1 ? 'and' : ''
            }}</span>
            <button
              type="button"
              class="craft"
              :class="
                i === active
                  ? 'craft--active text-ternary-dark dark:text-primary-light'
                  : 'text-gray-500'
              "
              :style="{ '--accent': craft.accent }"
              @mouseenter="select(i)"
              @focus="select(i)"
              @click="select(i)"
            >
              {{ craft.word
              }}<span class="text-gray-400">{{ i === crafts.length - 1 ? '!' : ',' }}</span>
            </button>
          </template>
        </p>

        <div class="enter mt-5 h-16 md:mt-6" :style="{ '--enter-delay': '240ms' }">
          <Transition name="blurb" mode="out-in">
            <p
              :key="active"
              class="mx-auto max-w-md text-base leading-relaxed text-ternary-dark dark:text-ternary-light md:mx-0"
            >
              {{ crafts[active].blurb }}
            </p>
          </Transition>
        </div>

        <div
          class="enter mt-7 flex flex-col items-center gap-3 sm:flex-row md:justify-start"
          :style="{ '--enter-delay': '320ms' }"
        >
          <a
            download
            href="/files/Resume.pdf"
            class="group inline-flex items-center gap-2 rounded-xl bg-jelly-500 px-6 py-3 font-general-medium text-white shadow-lg shadow-jelly-500/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-jelly-600"
          >
            <CustomIcon
              name="arrow-down"
              class="h-5 w-5 transition-transform group-hover:translate-y-0.5"
            />
            Resume
          </a>
          <span
            class="mt-4 h-px w-12 bg-gray-200 dark:bg-white/10 sm:hidden"
            aria-hidden="true"
          ></span>
          <a
            download
            href="/files/Thesis.pdf"
            class="group inline-flex items-center gap-2 py-1 font-general-medium text-ternary-dark transition-all duration-300 hover:-translate-y-0.5 hover:text-jelly-500 dark:text-ternary-light sm:rounded-xl sm:border sm:border-gray-300 sm:px-6 sm:py-3 sm:hover:border-jelly-500 sm:dark:border-ternary-dark"
          >
            Master's Thesis
            <CustomIcon
              name="arrow-up-right"
              class="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </a>
        </div>
      </div>

      <div class="flex w-full flex-col items-center md:w-1/2">
        <div class="stage" @mouseenter="stop" @mouseleave="start" @focusin="stop" @focusout="start">
          <span class="stage__halo" aria-hidden="true"></span>
          <span class="stage__ring" aria-hidden="true"></span>
          <div class="wheel" :style="{ transform: `rotate(${rotation}deg)` }">
            <button
              v-for="(craft, i) in crafts"
              :key="craft.id"
              type="button"
              class="pose"
              :class="{ 'pose--active': i === active }"
              :style="{ '--i': i }"
              :aria-label="`Show the ${craft.word} illustration`"
              :tabindex="i === active ? 0 : -1"
              @click="select(i)"
            >
              <img :src="craft.image" :alt="`Kadir ${craft.word} illustration`" draggable="false" />
            </button>
          </div>
        </div>

        <div class="mt-5 flex items-center gap-2.5">
          <button
            v-for="(craft, i) in crafts"
            :key="`dot-${craft.id}`"
            type="button"
            class="dot"
            :class="{ 'dot--active': i === active }"
            :style="{ '--accent': craft.accent }"
            :aria-label="`Show ${craft.word}`"
            @click="select(i)"
          ></button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { onMounted } from 'vue';

import CustomIcon from '@/components/base/CustomIcon.vue';
import { usePoseRotator } from '@/composables/usePoseRotator';
import { crafts } from '@/constants/crafts';
import { prefersReducedMotion } from '@/utils/motion';

const { active, rotation, select, start, stop } = usePoseRotator(crafts.length);

onMounted(() => {
  if (!prefersReducedMotion()) start();
});
</script>

<style scoped lang="scss">
.enter {
  animation: enter var(--dur-slow) cubic-bezier(0.2, 0.7, 0.2, 1) var(--enter-delay, 0s) both;
}

@keyframes enter {
  from {
    opacity: 0;
    transform: translateY(22px);
  }
}

.craft {
  padding: 0 0.1em;
  position: relative;
  transition: color 0.35s ease;

  &::after {
    background-color: var(--accent);
    border-radius: 9999px;
    bottom: 0.04em;
    content: '';
    height: 0.16em;
    left: 0.1em;
    position: absolute;
    right: 0.35em;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform var(--dur) cubic-bezier(0.2, 0.7, 0.2, 1);
  }

  &--active::after {
    transform: scaleX(1);
  }

  @media (hover: hover) {
    &:hover {
      color: var(--accent);
    }
  }
}

.stage {
  --r: clamp(5.5rem, 16vw, 7.5rem);

  height: min(26rem, 88vw);
  overflow: hidden;
  position: relative;
  width: min(24rem, 82vw);
}

.stage__halo {
  aspect-ratio: 1;
  background: radial-gradient(circle, rgb(var(--jelly-rgb) / 14%), transparent 68%);
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 78%;
}

.stage__ring {
  @apply border-jelly-500;

  aspect-ratio: 1;
  border-radius: 9999px;
  border-style: dashed;
  border-width: 2px;
  left: 50%;
  opacity: 0.18;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 86%;
}

.wheel {
  height: 0;
  left: 50%;
  position: absolute;
  top: calc(50% + var(--r));
  transition: transform 0.75s cubic-bezier(0.45, 0.05, 0.2, 1);
  width: 0;
}

.pose {
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  transform: rotate(calc(var(--i) * 120deg)) translateY(calc(-1 * var(--r))) translate(-50%, -50%);
  transform-origin: 0 0;
  transition: opacity 0.35s ease;
  width: clamp(10.5rem, 36vw, 13.5rem);

  img {
    filter: drop-shadow(0 16px 20px rgb(var(--shadow-rgb) / 18%));
    height: auto;
    pointer-events: none;
    width: 100%;
  }

  &--active {
    opacity: 1;
    transition: opacity 0.45s ease 0.33s;
  }
}

.dot {
  @apply bg-gray-300;

  border-radius: 9999px;
  height: 0.7rem;
  transition:
    width var(--dur) ease,
    background-color var(--dur) ease;
  width: 0.7rem;

  &--active {
    background-color: var(--accent);
    width: 1.8rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .enter {
    animation: none;
  }

  .wheel,
  .pose {
    transition: none;
  }
}
</style>
